import React, { useEffect, useState } from 'react';
import { WifiOff, Check, RefreshCw } from 'lucide-react';
import { getQueueLength } from '../../services/offlineSync';
import { useConexion } from '../../hooks/useConexion';
import { useIdioma } from '../../i18n/idioma';

/**
 * El aviso de «no hay conexión».
 *
 * ── Por qué avisa tan claro ─────────────────────────────────────────────────
 *
 * Antes era una pastilla gris de 20 px arriba a la derecha que decía «Offline».
 * Dos cosas iban mal con eso.
 *
 * La primera: estaba en inglés, con los textos escritos a mano en el
 * componente. En una app que se usa en español, y sin pasar por el diccionario,
 * así que las pruebas de traducción tampoco lo veían.
 *
 * La segunda es más seria. Sin conexión, de las doce pantallas que guardan
 * datos SOLO el reloj de entrada y salida sobrevive: lo demás —un estimado, un
 * cliente, un gasto— falla y se pierde lo que se escribió. Una pastilla gris
 * discreta no es un aviso proporcionado a eso. Quien está llenando un estimado
 * en una obra tiene derecho a saber, ANTES de escribir veinte partidas, que no
 * se van a guardar.
 *
 * Por eso el aviso es amarillo, ancho y no se puede cerrar. Un aviso que se
 * puede quitar se quita, y el dato se pierde igual.
 *
 * ── Dónde se coloca ─────────────────────────────────────────────────────────
 *
 * Centrado y por debajo de la barra superior del teléfono (h-14). Antes iba a
 * `top-2 right-4`, o sea encima de la campana y del avatar de esa misma barra.
 */
const OfflineBadge: React.FC = () => {
    const { t } = useIdioma();
    const { enLinea, comprobando, comprobar } = useConexion();
    const [pendientes, setPendientes] = useState(0);
    const [recienSubido, setRecienSubido] = useState(false);

    useEffect(() => {
        const mirar = () => {
            const largo = getQueueLength();
            setPendientes(anterior => {
                // La cola acaba de vaciarse: se confirma y el aviso se va solo.
                if (anterior > 0 && largo === 0) {
                    setRecienSubido(true);
                    window.setTimeout(() => setRecienSubido(false), 3000);
                }
                return largo;
            });
        };
        mirar();
        const id = window.setInterval(mirar, 3000);
        return () => window.clearInterval(id);
        // Sin `pendientes` en las dependencias: se leía dentro del efecto y el
        // intervalo se recreaba en cada cambio, reiniciando el reloj sin parar.
    }, []);

    const caja = 'pointer-events-auto flex items-center gap-2 px-3.5 py-2 rounded-xl text-[13px] font-semibold shadow-s2';

    if (recienSubido) {
        return (
            <div className={`${caja} bg-exito-bg text-exito animate-in fade-in duration-200`} role="status">
                <Check size={15} />
                <span>{t('conexion.yaSubio')}</span>
            </div>
        );
    }

    if (!enLinea) {
        return (
            /* `role="alert"`: esto interrumpe a propósito. Un lector de pantalla
               tiene que anunciarlo aunque el foco esté en otra parte — quien no
               ve la pantalla es quien más necesita enterarse de que lo que está
               escribiendo no se va a guardar. */
            <div className={`${caja} bg-aviso-bg text-aviso`} role="alert">
                <WifiOff size={15} className="shrink-0" />
                <span className="min-w-0">
                    {t('conexion.sinRed')}
                    {pendientes > 0 && <> · {t('conexion.pendientes', { count: pendientes })}</>}
                </span>
                <button
                    type="button"
                    onClick={comprobar}
                    disabled={comprobando}
                    className="shrink-0 ml-1 inline-flex items-center gap-1 rounded-lg px-2 py-1 hover:bg-aviso/10 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                >
                    <RefreshCw size={13} className={comprobando ? 'animate-spin' : ''} />
                    {t('conexion.reintentar')}
                </button>
            </div>
        );
    }

    // Con conexión pero con cosas sin subir: la cola se vacía sola, así que
    // esto informa, no alarma.
    if (pendientes > 0) {
        return (
            <div className={`${caja} bg-acero-100 text-acero-600`} role="status">
                <WifiOff size={15} />
                <span>{t('conexion.pendientes', { count: pendientes })}</span>
            </div>
        );
    }

    return null;
};

export default OfflineBadge;
